(() => {
  if (customElements.get('fab-quick-book')) return;
  const API = 'https://flyawayballooning-system.com';
  const titles = ['Private Charter', 'Any Day', 'Flexible Weekday', 'Weekday Morning'];
  const money = value => new Intl.NumberFormat('en-GB', {style:'currency',currency:'GBP',maximumFractionDigits:2,minimumFractionDigits:Number.isInteger(value)?0:2}).format(value);
  const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const active = v => v.is_active !== 0 && v.is_active !== false && v.is_active !== '0';
  const price = v => Number(v.has_sale_price && v.sale_price != null ? v.sale_price : v.price_per_person);
  const icons = {
    calendar: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2m0 16H5V10h14z"/></svg>',
    time: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20m0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16m1-13h-2v6l5 3 1-1.7-4-2.3z"/></svg>',
    validity: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M22 10V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v4a2 2 0 0 1 0 4v4c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-4a2 2 0 0 1 0-4m-2-1.5a4 4 0 0 0 0 7V18H4v-2.5a4 4 0 0 0 0-7V6h16z"/></svg>'
  };
  const detail = (icon,label) => `<span class="fab-qb-detail">${icons[icon]}<span>${escape(label)}</span></span>`;
  class QuickBook extends HTMLElement {
    connectedCallback() {
      this.observeWidget();
      if (this.ready) { if (this.mode && (this.mode !== 'book-date' || this.location())) this.load(); else this.render(); return; }
      this.ready = true;
      this.mode = '';
      this.selected = '';
      this.passengers = 1;
      this.rows = [];
      this.refundable = {};
      this.version = 0;
      this.charterQuotePassengers = null;
      this.priceError = false;
      this.addEventListener('change', event => this.change(event));
      this.addEventListener('click', event => this.clickControl(event));
      this.render();
    }
    disconnectedCallback() {
      this.version++;
      this.controller?.abort();
      this.widgetObserver?.disconnect();
      this.widgetObserver = null;
      document.body.classList.remove('fab-qb-visible');
    }
    observeWidget() {
      if (this.widgetObserver || !('IntersectionObserver' in window)) return;
      const section = this.closest('.fab-quick-book');
      if (!section) return;
      this.widgetObserver = new window.IntersectionObserver(entries => {
        document.body.classList.toggle('fab-qb-visible', entries[0].isIntersecting);
      });
      this.widgetObserver.observe(section);
    }
    location() { return this.dataset.fixedLocation || this.querySelector('[data-location]:checked')?.value || ''; }
    isFiesta() { return this.mode === 'book-date' && this.location() === 'Bristol Balloon Fiesta'; }
    selectedRow() { return this.rows.find(v => v.title === this.selected); }
    limits(v) { return {min:v.index === 0 ? 2 : 1, max:Math.max(v.index === 0 ? 2 : 1,Number(v.max_passengers) || 8)}; }
    clampPassengers(v) { const {min,max} = this.limits(v); this.passengers = Math.min(max,Math.max(min,this.passengers)); }
    refundAvailable(v) { return this.mode === 'book-date' && v.voucher_scope !== 'bristol_fiesta' && (v.index === 0 || v.index === 1); }
    refundEnabled(v) { return this.refundAvailable(v) && !!this.refundable[v.title]; }
    async request(path,signal) {
      const response = await fetch(API + path,{signal,credentials:'omit'});
      if (!response.ok) throw new Error('Pricing unavailable');
      const result = await response.json();
      if (!result.success || !Array.isArray(result.data)) throw new Error('Invalid pricing');
      return result.data.filter(active);
    }
    async load() {
      if (!this.mode || (this.mode === 'book-date' && !this.location())) { this.render(); return; }
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const version = ++this.version;
      this.loading = true;
      this.querySelector('[data-retry]').hidden = true;
      this.querySelector('[data-status]').textContent = 'Updating flight options...';
      this.setAttribute('aria-busy','true');
      this.render();
      const location = this.location() || 'Bath';
      const scope = this.isFiesta() ? 'bristol_fiesta' : 'standard';
      const params = new URLSearchParams({location,voucherScope:scope});
      const quotePassengers = Math.max(2,this.passengers);
      const privateParams = new URLSearchParams({...Object.fromEntries(params),passengers:String(quotePassengers)});
      const timeout = setTimeout(() => controller.abort(),15000);
      try {
        const [shared,charter] = await Promise.all([
          this.request('/api/voucher-types?' + params,controller.signal),
          this.request('/api/private-charter-voucher-types?' + privateParams,controller.signal)
        ]);
        if (version !== this.version) return;
        const availableTitles = scope === 'bristol_fiesta' ? ['Private Charter','Shared'] : titles;
        this.rows = availableTitles.map((title,index) => {
          const row = (index === 0 ? charter : shared).find(v => v.title.trim() === title && (v.voucher_scope || 'standard') === scope);
          if (!row || !Number.isFinite(price(row)) || price(row) <= 0) return null;
          return {...row,title,index};
        }).filter(row => row && !(this.mode === 'buy-gift' && /^standby flight/i.test(row.title)));
        if (!this.rows.length) throw new Error('No packages');
        this.charterQuotePassengers = quotePassengers;
        this.priceError = false;
        if (this.selectedRow()) this.clampPassengers(this.selectedRow());
        this.loading = false;
        this.querySelector('[data-status]').textContent = '';
      } catch (error) {
        if (version !== this.version) return;
        this.rows = [];
        this.loading = false;
        this.querySelector('[data-status]').textContent = 'Flight options are temporarily unavailable. Please try again or call 01823 778 127.';
        this.querySelector('[data-retry]').hidden = false;
      } finally {
        clearTimeout(timeout);
        if (version === this.version) { this.setAttribute('aria-busy','false'); this.render(); }
      }
    }
    async refreshCharterPrice() {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const version = ++this.version;
      const quotePassengers = Math.max(2,this.passengers);
      this.loading = true;
      this.priceError = false;
      this.querySelector('[data-retry]').hidden = true;
      this.querySelector('[data-status]').textContent = '';
      this.renderSelection();
      const params = new URLSearchParams({location:this.location() || 'Bath',voucherScope:this.isFiesta() ? 'bristol_fiesta' : 'standard',passengers:String(quotePassengers)});
      const timeout = setTimeout(() => controller.abort(),15000);
      try {
        const charter = await this.request('/api/private-charter-voucher-types?' + params,controller.signal);
        if (version !== this.version) return;
        const row = charter.find(v => v.title.trim() === 'Private Charter' && (v.voucher_scope || 'standard') === (this.isFiesta() ? 'bristol_fiesta' : 'standard'));
        if (!row || !Number.isFinite(price(row)) || price(row) <= 0) throw new Error('Private charter pricing unavailable');
        const index = this.rows.findIndex(v => v.index === 0);
        if (index < 0) throw new Error('Private charter unavailable');
        this.rows[index] = {...row,title:'Private Charter',index:0};
        this.charterQuotePassengers = quotePassengers;
      } catch (error) {
        if (version !== this.version) return;
        this.priceError = true;
        this.querySelector('[data-status]').textContent = 'Private charter price is temporarily unavailable. Please try again.';
        this.querySelector('[data-retry]').hidden = false;
      } finally {
        clearTimeout(timeout);
        if (version === this.version) { this.loading = false; this.renderSelection(); }
      }
    }
    change(event) {
      const input = event.target;
      if (input.name === 'mode-' + this.dataset.sectionId) {
        this.resetChoice();
        this.mode = input.value;
        if (!this.dataset.fixedLocation) this.querySelector('[data-location][value=""]').checked = true;
        if (this.mode === 'book-date' && !this.location()) this.render(); else this.load();
      }
      else if (input.matches('[data-location]')) { this.resetChoice(); if (this.location()) this.load(); else this.render(); }
      else if (input.matches('[data-package]')) {
        if (this.mode === 'book-date' && !this.location()) return;
        this.selected = input.value;
        this.clampPassengers(this.selectedRow());
        if (this.selectedRow()?.index === 0 && this.charterQuotePassengers !== this.passengers) this.refreshCharterPrice();
        else { this.priceError = false; this.renderSelection(); }
      } else if (input.matches('[data-refundable]')) {
        this.refundable[this.selected] = input.checked;
        this.renderSelection();
        this.querySelector('[data-refundable]')?.focus({preventScroll:true});
      }
    }
    resetChoice() {
      this.controller?.abort();
      this.version++;
      this.selected = '';
      this.rows = [];
      this.passengers = 1;
      this.refundable = {};
      this.charterQuotePassengers = null;
      this.priceError = false;
      this.loading = false;
      this.querySelector('[data-status]').textContent = '';
      this.querySelector('[data-retry]').hidden = true;
      this.setAttribute('aria-busy','false');
    }
    clickControl(event) {
      if (event.target.closest('[data-terms-open]')) return this.openTerms();
      if (event.target.closest('[data-terms-close]')) return this.querySelector('[data-terms-dialog]')?.close();
      const dialog = this.querySelector('[data-terms-dialog]');
      if (event.target === dialog) return dialog.close();
      const info = event.target.closest('[data-info-toggle]');
      this.querySelectorAll('[data-info-toggle]').forEach(button => {
        const open = button === info && button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(open));
      });
      if (info) return;
      if (event.target.closest('[data-retry]')) return this.priceError ? this.refreshCharterPrice() : this.load();
      if (event.target.closest('[data-continue]')) return this.proceed();
      const step = event.target.closest('[data-passenger-step]');
      if (!step || this.loading) return;
      const row = this.selectedRow();
      if (!row) return;
      const {min,max} = this.limits(row);
      const next = Math.min(max,Math.max(min,this.passengers + Number(step.dataset.passengerStep)));
      if (next === this.passengers) return;
      this.passengers = next;
      if (row.index === 0) this.refreshCharterPrice(); else this.renderSelection();
    }
    render() {
      this.dataset.mode = this.mode;
      const location = this.location();
      const awaitingLocation = this.mode === 'book-date' && !location;
      const note = this.querySelector('[data-mode-note]');
      note.hidden = !this.mode;
      note.textContent = this.mode === 'book-date' ? (this.dataset.fixedLocation ? 'Flight → Availability → Date' : 'Location → Flight → Availability → Date') : this.mode === 'buy-gift' ? 'Flight → Gift' : this.mode === 'flight-voucher' ? (this.dataset.fixedLocation ? 'Flight → Choose Date Later' : 'Flight → Choose Date/Location Later') : '';
      const options = this.querySelector('[data-options]');
      options.hidden = !this.mode || awaitingLocation || !this.rows.length;
      const optionsHeading = this.querySelector('[data-options-heading]');
      if (optionsHeading) {
        optionsHeading.hidden = options.hidden;
        optionsHeading.textContent = this.mode === 'book-date' ? 'Choose your flight' : 'Choose your flight voucher';
      }
      options.innerHTML = this.rows.map(v => {
        const selected = this.selected === v.title;
        const unit = price(v);
        const fiesta = v.voucher_scope === 'bristol_fiesta';
        const badge = v.index === 0 || fiesta ? '' : ['','Most flexible','Best value','Weekday Morning'][v.index];
        const badges = v.index === 0 ? '' : `<span class="fab-qb-option-badges"><span class="fab-qb-option-badge">Shared</span>${badge ? `<span class="fab-qb-option-badge fab-qb-option-badge--benefit">${badge}</span>` : ''}</span>`;
        const days = fiesta && v.index === 0 ? 'Bristol Fiesta' : v.flight_days || (v.index >= 2 ? 'Monday - Friday' : 'Any Day');
        const time = String(v.flight_time || '').toUpperCase() === 'AM & PM' ? 'AM or PM' : v.flight_time || '';
        const validity = Number(v.validity_months);
        const meta = [detail('calendar',days),time ? detail('time',time) : '',validity > 0 ? detail('validity','Validity: ' + validity + ' months') : ''].join('');
        const refundText = selected && this.refundEnabled(v) ? 'Weather refundable' : 'Non-refundable';
        return `<label class="fab-qb-option ${v.index === 0 ? 'fab-qb-option--private' : ''} ${selected ? 'is-selected' : ''}"><input type="radio" name="package-${escape(this.dataset.sectionId)}" data-package value="${escape(v.title)}" ${selected ? 'checked' : ''}><span class="fab-qb-option-body"><span class="fab-qb-option-head"><span class="fab-qb-option-title">${escape(v.title)}</span>${badges}</span><span class="fab-qb-option-details">${meta}</span></span><span class="fab-qb-option-price">${v.index === 0 && selected && this.priceError ? 'Unavailable' : `${v.has_sale_price && Number(v.original_price) > unit ? `<del>${money(Number(v.original_price))}</del>` : ''}${money(unit)}<small>${v.index === 0 ? selected ? 'total for ' + this.passengers : 'from 2 passengers' : 'per person'}</small>`}</span><span class="fab-qb-option-policy"><span class="fab-qb-positive">${v.index === 4 ? 'Last-minute opportunities' : fiesta ? 'Standard Flight Re-Booking' : 'Re-bookable'}</span><span class="fab-qb-negative ${refundText === 'Weather refundable' ? 'is-refundable' : ''}">${refundText}</span></span></label>`;
      }).join('');
      this.renderSummary();
    }
    renderSelection() {
      this.querySelectorAll('.fab-qb-option').forEach(option => {
        const input = option.querySelector('[data-package]');
        if (!input) return;
        const selected = input.value === this.selected;
        const row = this.rows.find(v => v.title === input.value);
        option.classList.toggle('is-selected',selected);
        input.checked = selected;
        if (row?.index === 0) {
          const priceLabel = option.querySelector('.fab-qb-option-price');
          if (priceLabel) priceLabel.innerHTML = selected && this.priceError ? 'Unavailable' : `${row.has_sale_price && Number(row.original_price) > price(row) ? `<del>${money(Number(row.original_price))}</del>` : ''}${money(price(row))}<small>${selected ? 'total for ' + this.passengers : 'from 2 passengers'}</small>`;
        }
        const policy = option.querySelector('.fab-qb-negative');
        if (policy) {
          const refundable = selected && this.refundEnabled(row);
          policy.textContent = refundable ? 'Weather refundable' : 'Non-refundable';
          policy.classList.toggle('is-refundable',refundable);
        }
      });
      this.renderSummary();
    }
    selectTermsRow(rows, voucher) {
      const id = Number(voucher.id);
      const activeRows = rows.filter(row => row && (row.is_active === 1 || row.is_active === true) && row.content);
      const matchesId = row => [row.voucher_type_id, ...(Array.isArray(row.voucher_type_ids) ? row.voucher_type_ids : []), ...(Array.isArray(row.private_voucher_type_ids) ? row.private_voucher_type_ids : [])].some(value => Number(value) === id);
      return activeRows.find(matchesId) || activeRows.find(row => String(row.title || '').trim().toLowerCase() === String(voucher.title || '').trim().toLowerCase()) || activeRows.find(row => row.is_default === 1 || row.is_default === true);
    }
    async openTerms() {
      const voucher = this.selectedRow();
      const dialog = this.querySelector('[data-terms-dialog]');
      const body = this.querySelector('[data-terms-body]');
      if (!voucher || !dialog || !body) return;
      this.querySelector('[data-terms-title]').textContent = `${voucher.title} — Terms & Conditions`;
      body.textContent = 'Loading terms…';
      if (!dialog.open) dialog.showModal();
      try {
        if (!this.termsRowsPromise) this.termsRowsPromise = this.requestTerms();
        const rows = await this.termsRowsPromise;
        if (!dialog.open || this.selectedRow()?.id !== voucher.id) return;
        const content = this.selectTermsRow(rows, voucher)?.content;
        body.replaceChildren();
        if (!content) throw new Error('Terms unavailable');
        const lines = String(content).split(/\n+/).map(line => line.trim().replace(/^[➟✓✔•\-–—]\s*/, '')).filter(Boolean);
        const list = document.createElement('ul');
        lines.forEach(line => { const item = document.createElement('li'); item.textContent = line; list.appendChild(item); });
        body.appendChild(list);
      } catch (error) {
        this.termsRowsPromise = null;
        body.replaceChildren();
        const link = document.createElement('a');
        link.href = '/pages/terms-conditions';
        link.textContent = 'View full terms and conditions';
        body.append('Terms are temporarily unavailable here. ', link);
      }
    }
    async requestTerms() {
      const response = await fetch(`${API}/api/terms-and-conditions`, {credentials:'omit',headers:{Accept:'application/json'}});
      if (!response.ok) throw new Error('Terms unavailable');
      const result = await response.json();
      if (!result.success || !Array.isArray(result.data)) throw new Error('Terms unavailable');
      return result.data;
    }
    renderSummary() {
      const row = this.selectedRow();
      const location = this.location();
      const summary = this.querySelector('[data-summary]');
      if (!row || (row.index === 0 && this.priceError)) { summary.hidden = true; return; }
      summary.hidden = false;
      const {min,max} = this.limits(row);
      const baseTotal = row.index === 0 ? price(row) : price(row) * this.passengers;
      const refundCost = Math.round((row.index === 0 ? baseTotal * 0.1 : 47.5 * this.passengers) * 100) / 100;
      const refundable = this.refundEnabled(row);
      const total = Math.round((baseTotal + (refundable ? refundCost : 0)) * 100) / 100;
      const unitTotal = row.index === 0 ? total : total / this.passengers;
      summary.innerHTML = `${row.index === 4 ? '<div style="padding:12px;background:#ecfdf5;border-radius:10px;margin:12px 0;color:#174b39"><strong>Normal value £180–£220</strong><p>We’ll contact you when we have space available on a flight. Local customers only; you must be able to travel at short notice.</p><details><summary>ⓘ How Standby Flight works</summary><p>This is not a bookable-date voucher. You cannot select a date in advance. Flight opportunities are offered when last-minute space becomes available.</p></details></div>' : ''}<div class="fab-qb-passenger-row"><span>Passengers:</span><div class="fab-qb-stepper" role="group" aria-label="Passengers"><button type="button" data-passenger-step="-1" aria-label="Remove passenger" ${this.loading || this.passengers <= min ? 'disabled' : ''}>−</button><output aria-label="Passenger count">${this.passengers}</output><button type="button" data-passenger-step="1" aria-label="Add passenger" ${this.loading || this.passengers >= max ? 'disabled' : ''}>+</button></div></div><div class="fab-qb-price-row" aria-live="polite"><span class="fab-qb-unit-price">${money(unitTotal)} <small>${row.index === 0 ? 'group price' : 'per person'}</small></span><span class="fab-qb-total-price"><small>Total</small><strong>${money(total)}</strong></span></div>${this.refundAvailable(row) ? `<label class="fab-qb-refund"><span>Make Refundable</span><span class="fab-qb-refund-controls"><span>${row.index === 0 ? '+10%' : '+£47.50 pp'}</span><input type="checkbox" role="switch" data-refundable aria-label="Make ${escape(row.title)} weather refundable" ${refundable ? 'checked' : ''} ${this.loading ? 'disabled' : ''}><span class="fab-qb-switch" aria-hidden="true"></span></span></label>` : ''}<div class="fab-qb-action"><button type="button" class="fab-qb-continue" data-continue ${this.loading || (this.mode === 'book-date' && !location) ? 'disabled' : ''}>${this.loading ? 'Updating price...' : this.mode === 'book-date' && !location ? 'Choose a Location' : row.index === 4 ? 'Choose Standby Flight' : this.mode === 'book-date' ? 'See Availability' : this.mode === 'buy-gift' ? 'Buy Gift' : 'Open Date'}</button></div><div class="fab-qb-support"><button type="button" class="fab-qb-terms-link" data-terms-open>Terms apply</button><span class="fab-qb-support-info"><button type="button" class="fab-qb-info-trigger" data-info-toggle aria-expanded="false" aria-label="About our guest ratings"><span aria-hidden="true">★</span> 5-star rated</button><span class="fab-qb-tooltip" role="tooltip">Rated by guests on Google, TripAdvisor &amp; Facebook.</span></span><span class="fab-qb-support-info"><span class="fab-qb-pay-label">Pay in 3 instalments</span><button type="button" class="fab-qb-info-trigger fab-qb-info-icon" data-info-toggle aria-expanded="false" aria-label="About Klarna payment options">i</button><span class="fab-qb-tooltip" role="tooltip">Choose Klarna at checkout to pay in 3 interest-free instalments, subject to eligibility.</span></span></div>`;
    }
    proceed() {
      const voucher = this.selectedRow();
      const location = this.mode === 'book-date' || this.dataset.fixedLocation ? this.location() : '';
      if (!voucher || this.loading || this.priceError || (voucher.index === 0 && this.charterQuotePassengers !== this.passengers) || (this.mode === 'book-date' && !location)) return;
      const fiesta = voucher.voucher_scope === 'bristol_fiesta';
      const voucherTitle = fiesta ? voucher.index === 0 ? 'Private Bristol Balloon Fiesta Flight' : 'Shared Bristol Balloon Fiesta Flight' : voucher.title;
      const params = new URLSearchParams({source:'shopify',voucherId:String(voucher.id),voucherTitle,passengers:String(this.passengers),experience:voucher.index === 0 ? 'Private Charter' : 'Shared Flight',startAt:this.mode === 'book-date' ? 'live-availability' : 'voucher-type'});
      if (location) params.set('location',location);
      if (fiesta) { params.set('voucherScope','bristol_fiesta'); params.set('calendarStart','next-fiesta'); }
      if (this.refundEnabled(voucher)) params.set('weatherRefundable','true');
      if (this.mode !== 'book-date') params.set('journey',this.mode);
      if (this.mode === 'buy-gift') { params.set('embed','gift-vouchers'); params.set('hideWeatherRefundablePresentation','true'); }
      if (voucher.index === 4) { params.set('journey','flight-voucher'); params.set('startAt','voucher-type'); params.delete('embed'); }
      const ads = typeof window.getGoogleAdsParamsForBooking === 'function' ? window.getGoogleAdsParamsForBooking() : null;
      if (ads) ['gclid','wbraid','gbraid','gad_source','gad_campaignid'].forEach(key => {if (ads[key]) params.set(key,ads[key]);});
      window.location.assign('https://flyawayballooning-book.com/?' + params);
    }
  }
  customElements.define('fab-quick-book',QuickBook);
})();
